/**
 * attachCameraFallback — une caméra débranchée en pleine séance : la salle bascule d'elle-même (weekly du 03/11).
 *
 * La règle est pure (`cameraFallback`) ; cette fonction la branche sur la salle, sans React :
 *  - **la liste change** (`MediaDevicesChanged`, que LiveKit émet au `devicechange` du navigateur) : si la caméra
 *    filmée n'y est plus, la salle passe sur la caméra de repli, **sans contrainte exacte** (`switchActiveDevice(…,
 *    false)`), pour que la prochaine perte ne fige plus rien ; rebranchée, la caméra choisie reprend sa place ;
 *  - **LiveKit coupe la caméra** juste après cette perte (`TrackMuted`) : c'est son `restartTrack()` qui a échoué
 *    sur l'appareil disparu, pas le geste de la personne. La caméra est rallumée sur le repli. Une coupure plus
 *    tardive, sans appareil perdu, ou après que la caméra a refilmé, reste celle de la personne : rien n'y touche.
 *
 * La caméra filmée se retient tant qu'elle filme (`getSettings().deviceId` d'une piste vivante) : `getActiveDevice`
 * ne suffit pas, LiveKit l'a parfois déjà déplacé quand la perte se voit. Chrome, la caméra choisie étant la première
 * qu'il connaissait, il pose seul la première restante (`selectDefaultDevices`), exacte, sans rallumer la caméra
 * (reproduit sur la box).
 *
 * **Un seul propriétaire des appareils de la salle** (simplify de #2980) : c'est `PrecallDeviceSync`, monté une fois
 * à la racine, qui l'attache, pour l'instant dans la seule salle du tuteur du design system, et ses bascules passent
 * par **sa** file (`run`) : deux `switchActiveDevice` concurrents se marcheraient dessus. Le micro n'est pas concerné : LiveKit le
 * rattrape seul (`restartOnDefaultAudioDevice`).
 */
import { type Participant, type Room, RoomEvent, Room as RoomClass, Track, type TrackPublication } from "livekit-client"

import { cameraFallback } from "./cameraFallback"

/** Une coupure de la caméra arrivée dans ce délai après la perte de l'appareil est celle de LiveKit. */
export const CAMERA_LOSS_WINDOW_MS = 10_000

export type CameraFallbackOptions = {
    /** La caméra choisie (au test d'avant séance ou dans la salle), lue au moment de basculer. */
    chosenId: () => string | null
    /** La file des bascules d'appareil de la salle : une à la fois. */
    run: (task: () => Promise<void>) => void
    /** Une bascule a eu lieu (une perte, ou le retour de la caméra choisie). */
    onSwitch?: (event: { lost: boolean; toChosen: boolean }) => void
}

/** Branche le repli de caméra sur la salle ; rend la fonction qui le débranche. */
export function attachCameraFallback(room: Room, options: CameraFallbackOptions): () => void {
    const { chosenId, run, onSwitch } = options
    let onFallback = false
    let lostAt: number | null = null
    let filmedId: string | null = null

    const camera = () => room.localParticipant?.getTrackPublication(Track.Source.Camera)

    // Une piste coupée ou finie ne dit plus rien : on garde la caméra qu'elle filmait. Une caméra qui filme
    // de nouveau désarme la perte : une coupure qui suivrait est celle de la personne, pas de LiveKit.
    const remember = () => {
        const publication = camera()
        const track = publication?.track?.mediaStreamTrack
        if (publication == null || publication.isMuted || track?.readyState !== "live") return
        filmedId = track.getSettings().deviceId || room.getActiveDevice("videoinput") || filmedId
        lostAt = null
    }

    // La caméra était allumée et LiveKit l'a coupée faute d'appareil : on la rallume, sur le repli.
    const relight = async () => {
        const publication = camera()
        if (lostAt == null || Date.now() - lostAt > CAMERA_LOSS_WINDOW_MS) return
        if (publication == null || !publication.isMuted) return
        lostAt = null
        await room.localParticipant.setCameraEnabled(true)
    }

    const balance = async () => {
        remember()
        const devices = await RoomClass.getLocalDevices("videoinput", false)
        const availableIds = devices.map((device) => device.deviceId)
        const lost = filmedId != null && !availableIds.includes(filmedId)
        const activeId = lost ? filmedId : room.getActiveDevice("videoinput")
        const chosen = chosenId()
        const next = cameraFallback({ activeId, chosenId: chosen, availableIds, onFallback })
        if (next == null) return
        if (lost) {
            lostAt = Date.now()
            filmedId = null
        }
        await room.switchActiveDevice("videoinput", next, false)
        // Le repli filme déjà (LiveKit n'avait pas coupé la caméra) : rien à rallumer, la perte est soldée.
        remember()
        onFallback = next !== chosen
        onSwitch?.({ lost, toChosen: next === chosen })
        await relight()
    }

    const onDevicesChanged = () => run(balance)
    const onCameraLive = () => remember()
    const onMuted = (publication: TrackPublication, participant: Participant) => {
        if (participant !== room.localParticipant || publication.source !== Track.Source.Camera) return
        run(async () => {
            // L'appareil a pu disparaître avant que la liste ne change : la coupure le dit d'abord.
            await balance()
            await relight()
        })
    }

    remember()
    room.on(RoomEvent.MediaDevicesChanged, onDevicesChanged)
    room.on(RoomEvent.TrackMuted, onMuted)
    room.on(RoomEvent.LocalTrackPublished, onCameraLive)
    room.on(RoomEvent.TrackUnmuted, onCameraLive)
    room.on(RoomEvent.ActiveDeviceChanged, onCameraLive)
    return () => {
        room.off(RoomEvent.MediaDevicesChanged, onDevicesChanged)
        room.off(RoomEvent.TrackMuted, onMuted)
        room.off(RoomEvent.LocalTrackPublished, onCameraLive)
        room.off(RoomEvent.TrackUnmuted, onCameraLive)
        room.off(RoomEvent.ActiveDeviceChanged, onCameraLive)
    }
}
